import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tree, Heart, Clock, Lightning, Users, PenNib, ArrowRight, Check, Spinner
} from '@phosphor-icons/react';
import axios from 'axios';
import VoiceTextarea from './VoiceTextarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NoiseOverlay = () => <div className="noise-overlay" />;

// Typewriter effect hook
const useTypewriter = (text, speed = 35) => {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    const interval = setInterval(() => {
      if (i < text.length) {
        setDisplayed(text.slice(0, i + 1));
        i++;
      } else {
        setDone(true);
        clearInterval(interval);
      }
    }, speed);
    return () => clearInterval(interval);
  }, [text, speed]);
  return { displayed, done };
};

// Single question card with typewriter prompt
const IkigaiQuestion = ({ question, value, onChange, icon: Icon, color, index, active }) => {
  const { displayed, done } = useTypewriter(active ? question.prompt : '', 30);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (done && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [done]);

  if (!active) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6 }}
      data-testid={`ikigai-q-${question.id}`}
    >
      {/* Question number */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center" style={{ border: `2px solid ${color}` }}>
          <Icon size={20} weight="duotone" style={{ color }} />
        </div>
        <div>
          <p className="text-overline" style={{ color, fontSize: '0.6rem' }}>
            FRAGE {index + 1} VON 5
          </p>
          <p className="text-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
            {question.category}
          </p>
        </div>
      </div>

      {/* Typewriter prompt */}
      <div className="mb-6 min-h-[4rem]">
        <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)', lineHeight: 1.6, color: 'var(--text-primary)' }}>
          {displayed}
          {!done && (
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              style={{ color }}
            >|</motion.span>
          )}
        </p>
      </div>

      {/* Hint */}
      {done && question.hint && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-mono text-xs mb-4"
          style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}
        >
          {question.hint}
        </motion.p>
      )}

      {/* Textarea */}
      {done && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <VoiceTextarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Schreibe oder sprich frei..."
            rows={5}
            testId={`ikigai-input-${question.id}`}
            style={{
              border: `1px solid ${value.length > 10 ? color : 'var(--border-default)'}`,
              transition: 'border-color 0.3s',
            }}
          />
          <div className="flex justify-between mt-2">
            <p className="text-mono text-xs" style={{ color: value.length > 10 ? color : 'var(--text-secondary)' }}>
              {value.length} Zeichen
            </p>
            {value.length < 10 && (
              <p className="text-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                Mindestens 10 Zeichen
              </p>
            )}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const IKIGAI_QUESTIONS = [
  {
    id: 'herkunft',
    category: 'DIE PRAEGUNG',
    prompt: 'Beschreibe das berufliche Umfeld deiner Kindheit. Was haben deine Eltern gemacht? Welches Narrativ ueber Arbeit wurde dir vorgelebt?',
    hint: 'Denke an die Atmosphaere zuhause. Was wurde ueber Erfolg, Geld und Arbeit gesagt — oder verschwiegen?',
    icon: Users,
    color: '#22C55E'
  },
  {
    id: 'ur_erinnerung',
    category: 'DIE UR-ERINNERUNG',
    prompt: 'Was ist deine allererste bewusste Erinnerung im Leben? Beschreibe das Gefuehl, nicht nur die Szene.',
    hint: 'Die erste Erinnerung ist oft ein Schluessel zu tiefsitzenden Werten und unbewussten Antrieben.',
    icon: Tree,
    color: '#A78BFA'
  },
  {
    id: 'leidenschaft',
    category: 'LEIDENSCHAFT VS. ZEIT',
    prompt: 'Wenn Geld keine Rolle spielte und du morgen 8 Stunden freie Zeit haettest: Was wuerdest du tun?',
    hint: 'Nicht was du tun "solltest". Was wuerdest du WIRKLICH tun, wenn niemand zusieht?',
    icon: Heart,
    color: '#EF4444'
  },
  {
    id: 'kompetenz_fokus',
    category: 'KOMPETENZ-FOKUS',
    prompt: 'Wofuer kommen Freunde, Familie oder Kollegen zu dir, um um Rat zu fragen? Was ist dein "Ding"?',
    hint: 'Oft sehen andere unsere Staerken klarer als wir selbst.',
    icon: Lightning,
    color: '#F59E0B'
  },
  {
    id: 'flow_moment',
    category: 'FLOW-ZUSTAND',
    prompt: 'Wann hast du das letzte Mal komplett das Zeitgefuehl verloren? Was hast du getan?',
    hint: 'Flow entsteht dort, wo Herausforderung und Faehigkeit sich treffen.',
    icon: Clock,
    color: '#06B6D4'
  }
];

const BiographicalDeepDive = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({
    herkunft: '', ur_erinnerung: '', leidenschaft: '', kompetenz_fokus: '', flow_moment: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const q = IKIGAI_QUESTIONS[currentQ];
  const currentAnswer = answers[q.id] || '';
  const canAdvance = currentAnswer.length >= 10;
  const isLast = currentQ === IKIGAI_QUESTIONS.length - 1;
  const allAnswered = IKIGAI_QUESTIONS.every(q => (answers[q.id] || '').length >= 10);

  const handleNext = () => {
    if (!canAdvance) return;
    if (isLast) return;
    setCurrentQ(c => c + 1);
  };

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/user/complete-biography`, {
        session_id: sessionId,
        ...answers
      });
      navigate(`/dashboard/${sessionId}`);
    } catch (err) {
      console.error('Biography submit error:', err);
      setError('Fehler beim Speichern. Bitte erneut versuchen.');
      setSubmitting(false);
    }
  };

  if (submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
        <NoiseOverlay />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
            <PenNib size={48} weight="duotone" style={{ color: '#A78BFA' }} />
          </motion.div>
          <p className="heading-large mt-6 mb-2">BIOGRAFIE WIRD ARCHIVIERT...</p>
          <p className="text-mono text-sm" style={{ color: 'var(--text-secondary)' }}>
            Deine Geschichte wird Teil des Archivs.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="page-shell-bg" data-testid="biographical-deep-dive">
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card"
      >
        {/* Header */}
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: '#A78BFA' }}>
              <Tree size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Block 4: Biografie</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Die Archaeologie des Ichs</p>
            </div>
          </div>
          <span className="text-xs px-3 py-1 font-semibold" style={{ border: '1px solid var(--border-default)', borderRadius: '9999px', color: 'var(--text-secondary)' }}>
            Ikigai
          </span>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex gap-1 mb-2">
            {IKIGAI_QUESTIONS.map((q, idx) => (
              <div key={q.id} className="flex-1 h-1 rounded-full transition-all cursor-pointer"
                onClick={() => { if (idx < currentQ || (answers[IKIGAI_QUESTIONS[idx].id] || '').length >= 10) setCurrentQ(idx); }}
                style={{
                  background: idx < currentQ ? q.color : idx === currentQ ? 'var(--text-primary)' : 'var(--border-default)'
                }} />
            ))}
          </div>
          <div className="flex justify-between">
            {IKIGAI_QUESTIONS.map((q, idx) => (
              <p key={q.id} style={{ fontSize: '0.5rem', color: idx === currentQ ? q.color : 'var(--text-secondary)', opacity: idx === currentQ ? 1 : 0.5 }}>
                {q.icon && React.createElement(q.icon, { size: 10 })}
              </p>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="page-shell-body">
          {currentQ === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 p-4" style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-default)', borderRadius: '12px' }}>
              <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                Die bisherigen Bloecke haben gemessen, was du KANNST. Dieser Block fragt, wer du BIST.
                Hier gibt es keine richtigen oder falschen Antworten — nur deine Geschichte. Nimm dir Zeit. Schreibe frei.
              </p>
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            <IkigaiQuestion key={currentQ} question={q} value={currentAnswer}
              onChange={(val) => setAnswers(prev => ({ ...prev, [q.id]: val }))}
              icon={q.icon} color={q.color} index={currentQ} active={true} />
          </AnimatePresence>

          <div className="mt-8 flex gap-3">
            {currentQ > 0 && (
              <button onClick={() => setCurrentQ(c => c - 1)}
                className="page-shell-btn page-shell-btn-outline"
                style={{ width: 'auto', padding: '0.75rem 1.5rem' }}
                data-testid="bio-prev">
                Zurueck
              </button>
            )}
            {!isLast ? (
              <button onClick={handleNext} disabled={!canAdvance}
                className="page-shell-btn flex-1" data-testid="bio-next">
                Weiter <ArrowRight size={18} weight="bold" />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={!allAnswered}
                className="page-shell-btn flex-1" data-testid="bio-submit">
                <Check size={18} weight="bold" /> Biografie abschliessen
              </button>
            )}
          </div>

          {error && (
            <p className="text-xs mt-3 text-center" style={{ color: 'var(--accent-cyan)' }}>{error}</p>
          )}

          {currentQ > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-8">
              <p className="text-xs font-semibold mb-3" style={{ color: 'var(--text-secondary)', letterSpacing: '0.08em' }}>Bereits beantwortet</p>
              <div className="space-y-2">
                {IKIGAI_QUESTIONS.slice(0, currentQ).map((q, idx) => (
                  <div key={q.id} className="flex items-center gap-2 p-2 cursor-pointer hover:bg-black/5 transition-all"
                    style={{ border: '1px solid var(--border-default)', borderRadius: '10px' }}
                    onClick={() => setCurrentQ(idx)}>
                    <q.icon size={14} style={{ color: q.color }} />
                    <p className="text-xs flex-1" style={{ color: 'var(--text-secondary)' }}>{q.category}</p>
                    <Check size={14} style={{ color: q.color }} />
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BiographicalDeepDive;
